import { useState, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de eventos que el backend puede enviar via SSE
// ─────────────────────────────────────────────────────────────────────────────

const STREAM_EVENT_TYPE = {
  CLASSIFICATION_COMPLETE: 'classification_complete',
  EXTRACTION_COMPLETE: 'extraction_complete',
  SCORE_COMPLETE: 'score_complete',
  VALIDATION_COMPLETE: 'validation_complete',
  WORKFLOW_ERROR: 'workflow_error',
  SESSION_EXPIRED: 'session_expired',
  // Emitido por el workflows-handler cuando detecta que la INE subida tiene solo 1 página.
  // El frontend debe solicitar al usuario que suba el reverso antes de continuar.
  INE_BACK_REQUIRED: 'ine_back_required',
  // Emitido por execute_pipeline tras la extracción con el score real del ai-service.
  // Sobreescribe el score provisional (fallback a approved_min) del evento score_complete.
  SCORE_UPDATE: 'score_update',
  // Emitido por execute_pipeline cuando el score de extracción cae por debajo del umbral
  // mínimo (human_review_min). El pipeline se detiene; el frontend debe mostrar la pantalla
  // de rechazo con motivo y permitir al usuario iniciar un nuevo proceso.
  EXTRACTION_REJECTED: 'extraction_rejected',
} as const;

type StreamEventType = (typeof STREAM_EVENT_TYPE)[keyof typeof STREAM_EVENT_TYPE];

// Forma genérica de un evento SSE recibido desde el workflows-handler
interface StreamEvent {
  type: StreamEventType;
  sessionId: string;
  payload: Record<string, unknown>;
}

// Resultado de scoring del ai-service (contenido de payload en SCORE_COMPLETE)
interface ScorePayload {
  score: number;
  passed: boolean;
  requiresPOA: boolean;
  details: Record<string, unknown>;
  rejectionReason?: string;
  // D-6: true si el score del documento cae en el rango de revisión humana (HUMAN_REVIEW).
  // El frontend lo acumula a lo largo del flujo; si es true al completar liveness,
  // llama a POST /kyc/human-review/{session_id} y transiciona a PENDING_HUMAN_REVIEW.
  requiresHumanReview?: boolean;
}

// Resultado de validación del core-api (contenido de payload en VALIDATION_COMPLETE)
interface ValidationPayload {
  status: 'approved' | 'rejected' | 'conditional';
  reason?: string;
  score: number;
  details: Record<string, unknown>;
}

// Payload del evento ine_back_required — referencia al anverso ya almacenado en GCS
interface IneBackRequiredPayload {
  filename: string;
  process_id: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Opciones y retorno del hook
// ─────────────────────────────────────────────────────────────────────────────

interface UseKYCStreamOptions {
  sessionId: string;
  token: string;
  apiBaseUrl: string;
  onEvent: (event: StreamEvent) => void;
  onError?: (error: Error) => void;
}

interface UseKYCStreamReturn {
  connected: boolean;
  // true mientras se espera un reintento de reconexión (entre el error y el próximo connect())
  reconnecting: boolean;
  lastEvent: StreamEvent | null;
}

// Máximo de reconexiones automáticas antes de llamar a onError y rendirse
const MAX_RECONNECT_ATTEMPTS = 3;

// Delay base para el backoff exponencial de reconexión (ms).
// Los delays resultantes son: 2 000 ms → 4 000 ms → 8 000 ms.
const RECONNECT_BASE_DELAY_MS = 2_000;

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────

export function useKYCStream(options: UseKYCStreamOptions): UseKYCStreamReturn {
  const { sessionId, token, apiBaseUrl } = options;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastEvent, setLastEvent] = useState<StreamEvent | null>(null);

  // Referencia al EventSource activo — no se usa useState para evitar re-renders
  const esRef = useRef<EventSource | null>(null);

  // Contador de intentos de reconexión
  const reconnectAttemptsRef = useRef(0);

  // Referencia al timer de reconexión para poder cancelarlo al desmontar
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mantiene la referencia actualizada al callback sin re-ejecutar el effect
  // Evita el problema de stale closure cuando el padre re-renderiza
  const onEventRef = useRef(options.onEvent);
  const onErrorRef = useRef(options.onError);

  useEffect(() => {
    onEventRef.current = options.onEvent;
  });

  useEffect(() => {
    onErrorRef.current = options.onError;
  });

  useEffect(() => {
    // EventSource no soporta headers personalizados en el estándar web;
    // se pasa el token como query param para la autenticación Bearer
    const url = `${apiBaseUrl}/api/v1/workflows/stream/${sessionId}?token=${encodeURIComponent(token)}`;

    function connect(): void {
      // Cierra cualquier conexión previa antes de abrir una nueva
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }

      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        // Conexión establecida — resetear contadores e indicador de reconexión
        setConnected(true);
        setReconnecting(false);
        reconnectAttemptsRef.current = 0;
      };

      es.onmessage = (event: MessageEvent<string>) => {
        try {
          const parsed = JSON.parse(event.data) as StreamEvent;
          setLastEvent(parsed);
          onEventRef.current(parsed);
        } catch {
          // Ignorar mensajes malformados — el backend puede enviar pings vacíos
        }
      };

      es.onerror = () => {
        // El EventSource perdió conexión o recibió un error del servidor
        setConnected(false);

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          // Se agotaron los reintentos — notificar al llamador y no volver a intentar
          setReconnecting(false);
          es.close();
          esRef.current = null;
          const error = new Error(
            `SSE: no se pudo reconectar después de ${MAX_RECONNECT_ATTEMPTS} intentos (session: ${sessionId})`
          );
          onErrorRef.current?.(error);
          return;
        }

        reconnectAttemptsRef.current += 1;
        setReconnecting(true);

        // Cierra la conexión fallida y programa un reintento con backoff exponencial:
        // intento 1 → 2 000 ms, intento 2 → 4 000 ms, intento 3 → 8 000 ms
        const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current - 1);
        es.close();
        esRef.current = null;

        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    }

    connect();

    // Limpieza al desmontar o cuando cambien sessionId / token / apiBaseUrl
    return () => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
      setReconnecting(false);
    };
  }, [sessionId, token, apiBaseUrl]);

  return { connected, reconnecting, lastEvent };
}

// Re-exportar tipos públicos para que los consumidores no necesiten redefinirlos
export { STREAM_EVENT_TYPE };
export type { StreamEventType, StreamEvent, ScorePayload, ValidationPayload, IneBackRequiredPayload };
